// The board kept across a layout switch (qa-runs 1199). Loading a layout — the Fight layout's offer, its Switch back, any
// preset — rebuilds dockview from nothing (`api.clear()`), which unmounted the board with every panel: its Pixi app was
// destroyed and a new one started from the view saved on the server (the last pan or zoom, still waiting its save delay,
// was dropped), while the new panel was still being laid out, so the board looked empty until Fit to screen. Now the board
// is rendered once, into a host element of its own that outlives the panels; the board's panel only lends it a place, and
// a rebuilt panel takes the same host — the same canvas, view and selection, never mounted again.
import { createContext, useContext, useLayoutEffect, useMemo, useRef, type ReactNode, type ReactPortal } from 'react';
import { createPortal } from 'react-dom';

/** The host the board is drawn into, or null where the board is not kept (the second window, a test's stand-in). */
export const KeptBoard = createContext<HTMLElement | null>(null);

/** The board rendered once into a host of its own: the host for `KeptBoard`, and the portal to render beside the dock. */
export function useKeptBoard(board: ReactNode, on: boolean): { host: HTMLElement | null; portal: ReactPortal | null } {
  const host = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.className = 'h-full';
    el.dataset.testid = 'kept-board';
    return el;
  }, []);
  return on && host ? { host, portal: createPortal(board, host) } : { host: null, portal: null };
}

/** Where the board shows: the kept host moved in while this place is mounted, or the board itself where none is kept. */
export function BoardPlace({ board }: { board: ReactNode }) {
  const host = useContext(KeptBoard);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const place = ref.current;
    if (!host || !place) return undefined;
    place.appendChild(host);
    return () => { if (host.parentNode === place) place.removeChild(host); };
  }, [host]);
  return host ? <div ref={ref} className="h-full" /> : <>{board}</>;
}

/**
 * What a panel keeps across a layout switch (qa-runs 1199), by the panel's own key: held by the dock, so it lasts as long
 * as the window, and absent (null) wherever a panel is drawn outside one — a panel there keeps nothing.
 */
export const PanelMemory = createContext<Map<string, unknown> | null>(null);
